import React from 'react';
import classnames from 'classnames';

import { Paper } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import ReviewSection from '@hydra/common/components/ReviewSection';
import * as CriteriaList from '@hydra/common/criteria';
import ReviewModule from '@hydra/common/components/Review/ReviewModule';
import { Image, Translate } from '@hydra/common/components';
import { useReviewContext } from '@hydra/common/context';

import { Crit, ReviewStructure } from '@hydra/utils/reviewStructure';

import { useResizeIframe } from '@hydra/common/customHooks';
import { ReviewData } from '@hydra/types/review';
import tadaImage from '@hydra/assets/img/review/tada.png';

const useStyles = makeStyles(theme => ({
  hide: { display: 'none' },
  show: { display: 'block' },
  emptyFilter: {
    padding: theme.spacing(2),
    textAlign: 'center'
  },
  mt15: {
    marginTop: theme.spacing(2)
  },
  img: {
    width: theme.spacing(2.5),
    marginLeft: theme.spacing(0.5)
  }
}));

interface ReviewProps {
  criteria: { [key in Crit]: any }
  components: { Module: React.FC<{ module: string, children: React.ReactNode }> }
  url: string
  competitors: ReviewData[]
  reviewStructure: ReviewStructure
  showCrit?: ({ crit, onpageReviewFilter }: { crit: Crit, onpageReviewFilter?: string | null }) => boolean
  showModule?: ({ module, section, onpageReviewFilter }: { module: string, section: string, onpageReviewFilter?: string | null }) => boolean
  showSection?: ({ section, onpageReviewFilter }: { section: string, onpageReviewFilter?: string | null }) => boolean
  onpageReviewFilter?: string | null
  outdatedCriteria?: { [key in Crit]: any }
}

export default function Review (props: ReviewProps) {
  const classes = useStyles();
  const {
    criteria,
    components: { Module = ReviewModule } = {},
    url,
    competitors,
    reviewStructure,
    showCrit = () => true,
    showModule = () => true,
    showSection = () => true,
    onpageReviewFilter = null,
    outdatedCriteria = {} as { [key in Crit]: any }
  } = props;
  const showSections = {
    on_page: showSection({ section: 'on_page', onpageReviewFilter }),
    off_page: showSection({ section: 'off_page', onpageReviewFilter })
  };
  const { isAssistant } = useReviewContext();

  const setElm = useResizeIframe();

  return (
    <>
      <div
        data-testid='review-component'
        className={(showSections.on_page || showSections.off_page) ? classes.show : classes.hide}
        ref={setElm}
      >
        {reviewStructure && reviewStructure.on_page && (
          <ReviewSection
            section='on_page'
            className={showSections.on_page ? classes.show : classes.hide}
          >
            {Object.keys(reviewStructure.on_page).map(module => {
              const crits = reviewStructure.on_page[module as keyof ReviewStructure['on_page']];
              return (
                <Module
                  module={module}
                  key={module}
                  className={showModule({ module, section: 'on_page', onpageReviewFilter }) ? classes.show : classes.hide}
                >
                  {crits.map((crit: Crit) => {
                    const Criteria = CriteriaList[crit];
                    const criterium = criteria[crit] ? { ...criteria[crit] } : criteria[crit];
                    if (criterium) {
                      criterium.isOutdated = !!outdatedCriteria[crit];
                    }
                    return (
                      <div className={showCrit({ crit, onpageReviewFilter }) ? classes.show : classes.hide} key={crit}>
                        <Criteria
                          criterium={criterium}
                          competitors={competitors}
                          url={url}
                        />
                      </div>
                    );
                  })}
                </Module>
              );
            })}
          </ReviewSection>
        )}
        {reviewStructure && reviewStructure.off_page && (
          <ReviewSection
            section='off_page'
            className={(showSections.off_page) ? classes.show : classes.hide}
          >
            {Object.keys(reviewStructure.off_page).map(module => {
              const crits = reviewStructure.off_page[module as keyof ReviewStructure['off_page']];
              return (
                <Module
                  module={module}
                  key={module}
                  className={showModule({ module, section: 'off_page', onpageReviewFilter }) ? classes.show : classes.hide}
                >
                  {crits.map((crit: Crit) => {
                    const Criteria = CriteriaList[crit];
                    const criterium = criteria[crit] ? { ...criteria[crit] } : criteria[crit];
                    if (criterium) {
                      criterium.isOutdated = !!outdatedCriteria[crit];
                    }
                    return (
                      <div className={showCrit({ crit, onpageReviewFilter }) ? classes.show : classes.hide} key={crit}>
                        <Criteria
                          criterium={criterium}
                          competitors={competitors}
                          url={url}
                        />
                      </div>
                    );
                  })}
                </Module>
              );
            })}
          </ReviewSection>
        )}
      </div>
      <div className={!showSections.on_page && !showSections.off_page ? classes.show : classes.hide}>
        <Paper className={classnames(classes.emptyFilter, isAssistant && classes.mt15)}>
          {
            isAssistant && onpageReviewFilter
              ? (
                  onpageReviewFilter === 'average'
                    ? (
                      <>
                        <Translate content='review_filter_assistant_empty_improve_state' />
                        <Image className={classes.img} src={tadaImage} />
                      </>
                      )
                    : (
                      <>
                        <Translate content='review_filter_assistant_empty_error_state' />
                        <Image className={classes.img} src={tadaImage} />
                      </>
                      )
                )
              : <Translate content='review_filter_empty_state' />
          }
        </Paper>
      </div>
    </>
  );
}
